import { useCallback } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withSpring,
  withTiming,
  Easing,
} from 'react-native-reanimated';

type ClickSparkProps = {
  children: React.ReactNode;
  onPress?: () => void;
  disabled?: boolean;
  sparkColor?: string;
};

const ANGLES = [0, 60, 120, 180, 240, 300];

function SparkParticle({
  angle,
  scale: scaleShared,
  opacity: opacityShared,
  r,
  g,
  b,
}: {
  angle: number;
  scale: Animated.SharedValue<number>;
  opacity: Animated.SharedValue<number>;
  r: number;
  g: number;
  b: number;
}) {
  const rad = (angle * Math.PI) / 180;
  const tx = Math.cos(rad) * 32;
  const ty = Math.sin(rad) * 32;

  const style = useAnimatedStyle(() => ({
    opacity: opacityShared.value,
    transform: [
      { translateX: tx * scaleShared.value },
      { translateY: ty * scaleShared.value },
      { scale: 0.3 + scaleShared.value * 0.7 },
    ],
  }));

  return (
    <Animated.View
      pointerEvents="none"
      style={[
        s.particle,
        { backgroundColor: `rgba(${r},${g},${b},0.9)` },
        style,
      ]}
    />
  );
}

export default function ClickSpark({
  children,
  onPress,
  disabled,
  sparkColor = '#60A5FA',
}: ClickSparkProps) {
  const pressScale = useSharedValue(1);
  const ringScale = useSharedValue(0.3);
  const ringOpacity = useSharedValue(0);

  const s0 = useSharedValue(0);
  const s1 = useSharedValue(0);
  const s2 = useSharedValue(0);
  const s3 = useSharedValue(0);
  const s4 = useSharedValue(0);
  const s5 = useSharedValue(0);
  const o0 = useSharedValue(0);
  const o1 = useSharedValue(0);
  const o2 = useSharedValue(0);
  const o3 = useSharedValue(0);
  const o4 = useSharedValue(0);
  const o5 = useSharedValue(0);

  const sparkScales = [s0, s1, s2, s3, s4, s5];
  const sparkOpacities = [o0, o1, o2, o3, o4, o5];

  const hex = sparkColor.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16) || 96;
  const g = parseInt(hex.slice(2, 4), 16) || 165;
  const b = parseInt(hex.slice(4, 6), 16) || 250;

  const fireSparks = useCallback(() => {
    ringOpacity.value = withSequence(
      withTiming(1, { duration: 50 }),
      withTiming(0, { duration: 360, easing: Easing.out(Easing.cubic) }),
    );
    ringScale.value = withSequence(
      withTiming(0.3, { duration: 0 }),
      withTiming(2.4, { duration: 420, easing: Easing.out(Easing.cubic) }),
    );
    sparkScales.forEach((sv) => {
      sv.value = withSequence(
        withTiming(0, { duration: 0 }),
        withSpring(1, { damping: 8, stiffness: 220 }),
        withTiming(0, { duration: 180, easing: Easing.in(Easing.cubic) }),
      );
    });
    sparkOpacities.forEach((ov) => {
      ov.value = withSequence(
        withTiming(1, { duration: 40 }),
        withTiming(0, { duration: 300, easing: Easing.out(Easing.cubic) }),
      );
    });
  }, []);

  const pressStyle = useAnimatedStyle(() => ({
    transform: [{ scale: pressScale.value }],
  }));

  const ringStyle = useAnimatedStyle(() => ({
    opacity: ringOpacity.value,
    transform: [{ scale: ringScale.value }],
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: `0 0 ${Math.round(ringOpacity.value * 22)}px rgba(${r},${g},${b},0.7)`,
        } as any)
      : {}),
  }));

  return (
    <Pressable
      disabled={disabled}
      onPressIn={() => {
        pressScale.value = withSpring(0.95, { damping: 15, stiffness: 320 });
      }}
      onPressOut={() => {
        pressScale.value = withSpring(1, { damping: 12, stiffness: 260 });
      }}
      onPress={() => {
        fireSparks();
        onPress?.();
      }}
    >
      <Animated.View style={pressStyle}>
        <View style={s.container}>
          {children}
          <Animated.View
            pointerEvents="none"
            style={[s.ring, { borderColor: `rgba(${r},${g},${b},0.85)` }, ringStyle]}
          />
          {ANGLES.map((angle, i) => (
            <SparkParticle
              key={angle}
              angle={angle}
              scale={sparkScales[i]}
              opacity={sparkOpacities[i]}
              r={r}
              g={g}
              b={b}
            />
          ))}
        </View>
      </Animated.View>
    </Pressable>
  );
}

const s = StyleSheet.create({
  container: {
    position: 'relative',
  },
  ring: {
    position: 'absolute',
    top: '50%' as any,
    left: '50%' as any,
    width: 64,
    height: 64,
    marginTop: -32,
    marginLeft: -32,
    borderRadius: 32,
    borderWidth: 1.5,
  },
  particle: {
    position: 'absolute',
    top: '50%' as any,
    left: '50%' as any,
    width: 5,
    height: 5,
    borderRadius: 2.5,
    marginTop: -2.5,
    marginLeft: -2.5,
  },
});
